import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  PageHeader,
  Typography,
  Space,
  Tooltip,
  Result,
  Spin,
  Modal,
  Input,
  Form,
  Tag,
  message,
  DatePicker,
  Select,
} from 'antd';
import {
  PlayCircleFilled,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  accessControls,
} from 'utils';
import {
  getProjectColor,
  getNoProjectColor,
} from 'utils/time_tracker_project_color';
import {
  timeTrackerProjectGetService,
  timeTrackerProjectsListService,
  timeTrackerEntryGetMyService,
  timeTrackerEntryStartService,
  timeTrackerEntryStopService,
  timeTrackerEntryGetRunningService,
  timeTrackerEntryUpdateService,
  timeTrackerEntryCreateManualService,
  timeTrackerEntryDeleteService,
} from 'services/time_tracker';
const { Text } = Typography;
const { TextArea } = Input;

function formatElapsed(startedAt) {
  if (!startedAt) return '0:00';
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const sec = Math.floor((now - start) / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatDuration(seconds) {
  if (seconds == null) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatTotalHours(seconds) {
  if (seconds == null || seconds === 0) return '0h';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

function getEntrySecondsForTable(entry) {
  const raw = entry.duration_seconds ?? entry.durationSeconds;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  if (entry.started_at && entry.stopped_at) {
    return Math.floor(
      (new Date(entry.stopped_at) - new Date(entry.started_at)) / 1000,
    );
  }
  return 0;
}

function parseDurationToSeconds(input) {
  const s = String(input ?? '').trim();
  if (!s) return null;
  const parts = s.split(':').map((p) => Number(String(p).trim()));
  if (parts.some((n) => !Number.isFinite(n) || n < 0)) return null;
  if (parts.length === 1) return Math.round(parts[0] * 60);
  if (parts.length === 2) return Math.round(parts[0] * 60 + parts[1]);
  return Math.round(parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0));
}

function secondsToDurationInput(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    x,
  ).padStart(2, '0')}`;
}

function ProjectInlineProjectPicker({ record, projectOptions, onSaved }) {
  const rawId = record.project?._id || record.project;
  const [val, setVal] = useState(rawId ? String(rawId) : undefined);
  useEffect(() => {
    const id = record.project?._id || record.project;
    setVal(id ? String(id) : undefined);
  }, [record._id, record.project]);
  const stopped = record.stopped_at != null;
  if (!stopped) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span
          className="clockify-project-dot"
          style={{
            background: record.project
              ? getProjectColor(record.project)
              : getNoProjectColor(),
          }}
        />
        {record.project?.project_name || '—'}
      </span>
    );
  }
  return (
    <Select
      size="small"
      className="clockify-table-project-select"
      style={{ minWidth: 168, maxWidth: 240 }}
      value={val || undefined}
      onChange={(v) => {
        setVal(v || undefined);
        timeTrackerEntryUpdateService(record._id, { project: v || null })
          .then(() => onSaved())
          .catch(handleApiError);
      }}
      options={projectOptions}
      allowClear
      showSearch
      optionFilterProp="label"
      notFoundContent={
        projectOptions.length === 0 ? 'No projects' : 'No matching projects'
      }
      dropdownMatchSelectWidth={320}
    />
  );
}

function ProjectInlineStart({ record, onSaved }) {
  const stopped = record.stopped_at != null;
  const [v, setV] = useState(() => moment(record.started_at));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(moment(record.started_at));
  }, [record._id, record.started_at]);
  if (!stopped) {
    return record.started_at
      ? moment(record.started_at).format('YYYY-MM-DD HH:mm')
      : '—';
  }
  return (
    <DatePicker
      showTime
      format="YYYY-MM-DD HH:mm"
      size="small"
      allowClear={false}
      inputReadOnly
      style={{ minWidth: 148 }}
      value={v}
      disabled={busy}
      onChange={(d) => {
        if (!d) return;
        setV(d);
        setBusy(true);
        timeTrackerEntryUpdateService(record._id, {
          started_at: d.toISOString(),
        })
          .then(() => onSaved())
          .catch(handleApiError)
          .finally(() => setBusy(false));
      }}
    />
  );
}

function ProjectInlineStop({ record, onSaved }) {
  const stopped = record.stopped_at != null;
  const [v, setV] = useState(() => moment(record.stopped_at));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(moment(record.stopped_at));
  }, [record._id, record.stopped_at]);
  if (!stopped) return '—';
  return (
    <DatePicker
      showTime
      format="YYYY-MM-DD HH:mm"
      size="small"
      allowClear={false}
      inputReadOnly
      style={{ minWidth: 148 }}
      value={v}
      disabled={busy}
      onChange={(d) => {
        if (!d) return;
        setV(d);
        setBusy(true);
        timeTrackerEntryUpdateService(record._id, {
          stopped_at: d.toISOString(),
        })
          .then(() => onSaved())
          .catch(handleApiError)
          .finally(() => setBusy(false));
      }}
    />
  );
}

function ProjectInlineDuration({ record, onSaved }) {
  const stopped = record.stopped_at != null;
  const [durStr, setDurStr] = useState(() =>
    secondsToDurationInput(getEntrySecondsForTable(record)),
  );
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDurStr(secondsToDurationInput(getEntrySecondsForTable(record)));
  }, [
    record._id,
    record.started_at,
    record.stopped_at,
    record.duration_seconds,
  ]);
  if (!stopped) return formatDuration(getEntrySecondsForTable(record));
  return (
    <Input
      size="small"
      style={{ width: 88, fontVariantNumeric: 'tabular-nums' }}
      title="HH:MM:SS or MM:SS; one number = minutes. Keeps start time; end shifts to match."
      value={durStr}
      disabled={busy}
      onChange={(e) => setDurStr(e.target.value)}
      onBlur={() => {
        const sec = parseDurationToSeconds(durStr);
        if (sec == null) {
          message.error('Duration: HH:MM:SS, MM:SS, or one number = minutes');
          setDurStr(secondsToDurationInput(getEntrySecondsForTable(record)));
          return;
        }
        if (sec === getEntrySecondsForTable(record)) return;
        setBusy(true);
        timeTrackerEntryUpdateService(record._id, { duration_seconds: sec })
          .then(() => onSaved())
          .catch(handleApiError)
          .finally(() => setBusy(false));
      }}
      onPressEnter={(e) => e.target.blur()}
    />
  );
}

function ProjectInlineTask({ record, onSaved, startButton }) {
  const [v, setV] = useState(record.task_description || '');
  useEffect(() => {
    setV(record.task_description || '');
  }, [record._id, record.task_description]);
  const save = () => {
    const t = (v ?? '').trim();
    if (t === (record.task_description || '').trim()) return;
    timeTrackerEntryUpdateService(record._id, { task_description: t })
      .then(() => onSaved())
      .catch(handleApiError);
  };
  return (
    <Space size="small" wrap>
      <Input
        bordered={false}
        style={{ minWidth: 160, maxWidth: 360 }}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={save}
        onPressEnter={save}
        placeholder="What are you working on?"
      />
      {startButton}
    </Space>
  );
}

export default function TimeTrackerProjectEntriesPage() {
  const { projectId } = useParams();
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [project, setProject] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [startingProjectId, setStartingProjectId] = useState(null);
  const [startingEntryId, setStartingEntryId] = useState(null);
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualForm] = Form.useForm();
  const [viewDetailEntry, setViewDetailEntry] = useState(null);
  const [runningEntry, setRunningEntry] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [stopModalVisible, setStopModalVisible] = useState(false);
  const [stopEntryId, setStopEntryId] = useState(null);
  const [stopSubmitting, setStopSubmitting] = useState(false);
  const [, setElapsedTick] = useState(0);
  const pendingTaskRef = useRef(null);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const loadProject = () => {
    if (!projectId) return;
    setLoading(true);
    timeTrackerProjectGetService(projectId)
      .then((res) => {
        setLoading(false);
        const items = res.data?.items || [];
        setProject(items[0] || null);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
        setProject(null);
      });
  };

  const loadEntries = useCallback(() => {
    if (!projectId) return;
    setEntriesLoading(true);
    timeTrackerEntryGetMyService({ project: projectId, limit: 200 })
      .then((res) => {
        setEntriesLoading(false);
        const data = res.data;
        const rawList = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];
        const list = [...rawList].sort((a, b) => {
          const ta = new Date(a?.started_at || 0).getTime();
          const tb = new Date(b?.started_at || 0).getTime();
          if (tb !== ta) return tb - ta;
          return String(b._id || '').localeCompare(String(a._id || ''));
        });
        setEntries(list);
      })
      .catch((err) => {
        setEntriesLoading(false);
        setEntries([]);
        handleApiError(err);
      });
  });

  const loadRunning = () => {
    timeTrackerEntryGetRunningService()
      .then((res) => {
        const items = res.data.items || [];
        setRunningEntry(items.length ? items[0] : null);
      })
      .catch(() => setRunningEntry(null));
  };

  const loadAllProjects = () => {
    timeTrackerProjectsListService()
      .then((res) => setAllProjects(res.data?.items || []))
      .catch(() => setAllProjects([]));
  };

  const projectPickerOptions = useMemo(() => {
    return [...allProjects]
      .sort((a, b) =>
        (a.project_name || '').localeCompare(b.project_name || '', undefined, {
          sensitivity: 'base',
        }),
      )
      .map((p) => ({
        value: p._id,
        label: (
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <span
              className="clockify-project-dot"
              style={{ background: getProjectColor(p) }}
            />
            <span>{p.project_name || p._id}</span>
          </span>
        ),
      }));
  }, [allProjects]);

  useEffect(() => {
    if (permissions == null || !projectId) return;
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    loadProject();
    loadAllProjects();
    loadEntries();
    loadRunning();
  }, [loadEntries, loadProject, permissions, projectId]);

  useEffect(() => {
    if (!runningEntry || stopModalVisible) return;
    const interval = setInterval(() => setElapsedTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [runningEntry, stopModalVisible]);

  const openStopModal = (entry) => {
    setStopEntryId(entry._id);
    setStopModalVisible(true);
  };

  const onStopTimerClick = (entry) => {
    openStopModal(entry);
  };

  const getStopModalInput = (id) => {
    const el = document.getElementById(id);
    if (!el) return null;
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return el;
    return el.querySelector('input, textarea') || el;
  };

  useEffect(() => {
    if (!stopModalVisible) return;
    const applyPending = () => {
      const pending = pendingTaskRef.current;
      const desc = pending?.task_description ?? '';
      const descEl = getStopModalInput('stop-modal-task-description');
      if (descEl) descEl.value = desc;
    };
    const t = setTimeout(applyPending, 0);
    return () => clearTimeout(t);
  }, [stopModalVisible]);

  const handleStop = () => {
    if (!stopEntryId) return;
    const descEl = getStopModalInput('stop-modal-task-description');
    const task_description = (descEl?.value ?? '').trim();
    const hadProject = !!(runningEntry?.project?._id || runningEntry?.project);
    if (!hadProject && !projectId) {
      message.error('Project is required to stop this timer');
      return;
    }
    setStopSubmitting(true);
    timeTrackerEntryStopService(stopEntryId, {
      task_title: '',
      task_description,
      ...(!hadProject && projectId ? { project: projectId } : {}),
    })
      .then((res) => {
        setStopSubmitting(false);
        setStopModalVisible(false);
        setStopEntryId(null);
        setRunningEntry(null);
        pendingTaskRef.current = null;
        handleApiSuccess(res);
        loadEntries();
      })
      .catch((err) => {
        setStopSubmitting(false);
        handleApiError(err);
      });
  };

  const handleStartForProject = (pid, sourceEntry) => {
    if (sourceEntry?.task_description) {
      pendingTaskRef.current = {
        task_description: sourceEntry.task_description,
      };
    } else {
      pendingTaskRef.current = null;
    }
    setStartingEntryId(sourceEntry?._id ?? null);
    setStartingProjectId(pid);
    timeTrackerEntryStartService({
      project: pid,
      task_description: sourceEntry?.task_description || '',
    })
      .then((res) => {
        setStartingProjectId(null);
        setStartingEntryId(null);
        handleApiSuccess(res);
        loadRunning();
      })
      .catch((err) => {
        setStartingProjectId(null);
        setStartingEntryId(null);
        handleApiError(err);
      });
  };

  const openViewDetail = (record) => setViewDetailEntry(record);

  const projectName = project?.project_name || project?._id || 'Project';

  const isRunningThisProject =
    runningEntry &&
    (runningEntry.project?._id || runningEntry.project) === projectId;

  const totalSeconds = useMemo(() => {
    return entries.reduce((sum, e) => {
      const raw = e.duration_seconds ?? e.durationSeconds;
      let sec = 0;
      if (raw != null && raw !== '') {
        const n = Number(raw);
        if (Number.isFinite(n)) sec = n;
      }
      if (sec <= 0 && e.started_at && e.stopped_at) {
        sec = Math.floor(
          (new Date(e.stopped_at) - new Date(e.started_at)) / 1000,
        );
      }
      return sum + (sec > 0 ? sec : 0);
    }, 0);
  }, [entries]);

  const handleStartNewTimer = () => {
    handleStartForProject(projectId, null);
  };

  const openManualModal = () => {
    const now = moment();
    manualForm.setFieldsValue({
      started_at: now.clone().subtract(1, 'hour'),
      stopped_at: now,
      task_description: '',
    });
    setManualModalVisible(true);
  };

  const handleManualSubmit = () => {
    manualForm
      .validateFields()
      .then((values) => {
        const started_at =
          values.started_at?.toISOString?.() ??
          moment(values.started_at).toISOString();
        const stopped_at =
          values.stopped_at?.toISOString?.() ??
          moment(values.stopped_at).toISOString();
        if (moment(stopped_at).isBefore(moment(started_at))) {
          message.error('Stop time must be after start time');
          return;
        }
        setManualSubmitting(true);
        timeTrackerEntryCreateManualService({
          project: projectId,
          started_at,
          stopped_at,
          task_title: '',
          task_description: (values.task_description ?? '').trim(),
        })
          .then((res) => {
            setManualSubmitting(false);
            setManualModalVisible(false);
            manualForm.resetFields();
            handleApiSuccess(res);
            loadEntries();
          })
          .catch((err) => {
            setManualSubmitting(false);
            handleApiError(err);
          });
      })
      .catch(() => {});
  };

  const entryColumns = [
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 168,
      render: (_, record) => (
        <ProjectInlineStart record={record} onSaved={loadEntries} />
      ),
    },
    {
      title: 'Stopped',
      dataIndex: 'stopped_at',
      key: 'stopped_at',
      width: 168,
      render: (_, record) => (
        <ProjectInlineStop record={record} onSaved={loadEntries} />
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <ProjectInlineDuration record={record} onSaved={loadEntries} />
      ),
    },
    {
      title: 'Project',
      key: 'project',
      width: 200,
      render: (_, record) => (
        <ProjectInlineProjectPicker
          record={record}
          projectOptions={projectPickerOptions}
          onSaved={loadEntries}
        />
      ),
    },
    {
      title: 'Task',
      key: 'task',
      ellipsis: true,
      minWidth: 200,
      render: (_, r) => (
        <ProjectInlineTask
          record={r}
          onSaved={loadEntries}
          startButton={
            !isRunningThisProject ? (
              <Tooltip title="Start a new timer with this task (same project)">
                <Button
                  type="link"
                  size="small"
                  icon={
                    <PlayCircleFilled
                      style={{ fontSize: 18, color: getProjectColor(project) }}
                    />
                  }
                  loading={
                    startingProjectId === projectId && startingEntryId === r._id
                  }
                  onClick={() => handleStartForProject(projectId, r)}
                >
                  Start again
                </Button>
              </Tooltip>
            ) : null
          }
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 108,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View task detail">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewDetail(record)}
            />
          </Tooltip>
          {record.stopped_at != null && (
            <Tooltip title="Delete this entry">
              <Button
                type="default"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Delete this time entry?',
                    content: 'This cannot be undone.',
                    okText: 'Delete',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: () =>
                      timeTrackerEntryDeleteService(record._id)
                        .then((res) => {
                          handleApiSuccess(res);
                          loadEntries();
                        })
                        .catch(handleApiError),
                  });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTracker.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view time entries."
          extra={
            <Button
              type="primary"
              onClick={() => history.push('/time-tracker/tracker')}
            >
              Back to Tracker
            </Button>
          }
        />
      </div>
    );
  }

  if (loading && !project) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!project && !loading) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="404"
          title="Project not found"
          extra={
            <Button onClick={() => history.push('/time-tracker/tracker')}>
              Back to Tracker
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 24px 24px' }}>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Time Tracker', path: '/time-tracker/tracker' },
          { label: 'Tracker', path: '/time-tracker/tracker' },
          { label: projectName, path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/time-tracker/tracker')}
        title={
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
          >
            <span
              className="clockify-project-dot"
              style={{ background: getProjectColor(project) }}
            />
            <span>{`My entries — ${projectName}`}</span>
          </span>
        }
        subTitle={
          <Space>
            <span>Your time entries for this project only</span>
            <Tag color="blue">Total time: {formatTotalHours(totalSeconds)}</Tag>
          </Space>
        }
        extra={
          <Space>
            {isRunningThisProject ? (
              <Button
                type="primary"
                danger
                icon={<PauseCircleOutlined />}
                onClick={() => onStopTimerClick(runningEntry)}
              >
                Stop Timer
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={
                  startingProjectId === projectId && startingEntryId === null
                }
                onClick={handleStartNewTimer}
              >
                Start new timer
              </Button>
            )}
            <Button icon={<PlusOutlined />} onClick={openManualModal}>
              Add manual time
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                loadEntries();
                loadRunning();
              }}
            >
              Refresh
            </Button>
          </Space>
        }
      />

      {isRunningThisProject && runningEntry && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space>
            <Tag color="blue">Timer running</Tag>
            <Text type="secondary">
              {formatElapsed(runningEntry.started_at)}
            </Text>
            <Button
              type="primary"
              danger
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => onStopTimerClick(runningEntry)}
            >
              Stop Timer
            </Button>
          </Space>
        </Card>
      )}

      <Card bodyStyle={{ padding: '16px 24px' }}>
        <Table
          rowKey="_id"
          dataSource={entries}
          columns={entryColumns}
          scroll={{ x: 1280 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} entries`,
          }}
          size="middle"
          loading={entriesLoading}
          locale={{ emptyText: 'No time entries for this project yet' }}
        />
      </Card>

      <Modal
        title="Task detail"
        open={!!viewDetailEntry}
        onCancel={() => setViewDetailEntry(null)}
        footer={[
          <Button key="close" onClick={() => setViewDetailEntry(null)}>
            Close
          </Button>,
        ]}
      >
        {viewDetailEntry && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary">Started</Text>
              <div>
                {viewDetailEntry.started_at
                  ? new Date(viewDetailEntry.started_at).toLocaleString()
                  : '-'}
              </div>
            </div>
            <div>
              <Text type="secondary">Project</Text>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 2,
                }}
              >
                <span
                  className="clockify-project-dot"
                  style={{
                    background: viewDetailEntry.project
                      ? getProjectColor(viewDetailEntry.project)
                      : getNoProjectColor(),
                  }}
                />
                {viewDetailEntry.project?.project_name || '—'}
              </div>
            </div>
            <div>
              <Text type="secondary">Stopped</Text>
              <div>
                {viewDetailEntry.stopped_at
                  ? new Date(viewDetailEntry.stopped_at).toLocaleString()
                  : '-'}
              </div>
            </div>
            <div>
              <Text type="secondary">Duration</Text>
              <div>{formatDuration(viewDetailEntry.duration_seconds)}</div>
            </div>
            <div>
              <Text type="secondary">Task description</Text>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {viewDetailEntry.task_description || '—'}
              </div>
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        title="Stop Timer"
        open={stopModalVisible}
        onOk={handleStop}
        onCancel={() => {
          setStopModalVisible(false);
          setStopEntryId(null);
        }}
        confirmLoading={stopSubmitting}
        okText="Stop & Save"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {runningEntry &&
          !(runningEntry.project?._id || runningEntry.project) ? (
            <div>
              <Text type="secondary">Project</Text>
              <div
                style={{
                  marginTop: 4,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  className="clockify-project-dot"
                  style={{ background: getProjectColor(project) }}
                />
                <span>
                  {project?.project_name || projectId}{' '}
                  <Text type="secondary" style={{ fontWeight: 400 }}>
                    {'(required — this page\u2019s project will be saved)'}
                  </Text>
                </span>
              </div>
            </div>
          ) : (
            <div>
              <Text type="secondary">Project</Text>
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  className="clockify-project-dot"
                  style={{
                    background: getProjectColor(
                      runningEntry?.project || project,
                    ),
                  }}
                />
                {runningEntry?.project?.project_name ||
                  project?.project_name ||
                  '—'}
              </div>
            </div>
          )}
          <div>
            <label>Task description (optional)</label>
            <TextArea
              id="stop-modal-task-description"
              rows={3}
              placeholder="What did you work on? (leave blank to edit later)"
              style={{ marginTop: 4 }}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title="Add manual time"
        open={manualModalVisible}
        onOk={handleManualSubmit}
        onCancel={() => {
          setManualModalVisible(false);
          manualForm.resetFields();
        }}
        confirmLoading={manualSubmitting}
        okText="Add entry"
        width={480}
      >
        <Form form={manualForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="started_at"
            label="Start date & time"
            rules={[{ required: true, message: 'Select start time' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="stopped_at"
            label="Stop date & time"
            rules={[{ required: true, message: 'Select stop time' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="task_description"
            label="Task description (optional)"
          >
            <TextArea rows={3} placeholder="What did you work on?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
